import React, { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  ContentContainer,
} from 'prosci-ui'
import { ExternalLink, Loader2 } from 'lucide-react'
import { Footer } from '../../components'
import { useApp } from '../../contexts/AppContext'
import { useHubAppCopy } from '../../hooks'
import { useHubSpotSnippet } from './useHubSpotSnippet'
import { useHubSpotConversion } from './useHubSpotConversion'

export type CheckoutConfirmationState = {
  method?: 'card' | 'invoice'
  invoicePdfUrl?: string
  dueDate?: string
  deferredStartDate?: string
  planName?: string
  flashMessage?: string
}

// Shared between the modal that writes and this page that reads. SessionStorage
// survives a refresh so an invoice user who reloads doesn't lose their invoice
// details — and, critically, the 8-second webhook-settling timer still fires.
export const CHECKOUT_CONFIRMATION_STORAGE_KEY = 'prosci.checkoutConfirmation'

function readStoredState(): CheckoutConfirmationState | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_CONFIRMATION_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as CheckoutConfirmationState) : null
  } catch {
    return null
  }
}

// Invoice path waits for async Stripe webhooks + provisioning to settle before
// letting the user continue. Card path doesn't need this — Stripe Checkout
// already settles before redirecting back.
const INVOICE_MIN_DISPLAY_MS = 8000

type GrantedConversionCopy = { discount?: string; deferred?: string; immediate?: string }

// convert_granted appends grantedConversion (and startDate when billing is deferred) to the success URL.
function grantedConversionFlash(params: URLSearchParams, copy: GrantedConversionCopy | undefined): string | null {
  switch (params.get('grantedConversion')) {
    case 'discount':
      return copy?.discount || 'Your subscription is now active! Your early adopter discount has been applied.'
    case 'deferred': {
      const startDate = formatDueDate(params.get('startDate') || '')
      return (copy?.deferred || "You're all set! Your paid membership starts {{startDate}}. You won't be charged until then.")
        .replace('{{startDate}}', startDate)
    }
    case 'immediate':
      return copy?.immediate || 'Your subscription is now active!'
    default:
      return null
  }
}

// Guards against `javascript:`/`data:` URLs being rendered into href.
function safeHttpHref(url: string | undefined): string | null {
  if (!url) return null
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.href
      : null
  } catch {
    return null
  }
}

function formatDueDate(dateString: string): string {
  if (!dateString) return ''
  try {
    // Stripe/Rails send due dates as UTC-midnight ISO strings. Formatting
    // without an explicit timeZone would render in local time and shift the
    // displayed calendar day backward for users west of UTC.
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
  } catch {
    return dateString
  }
}

function CheckoutConfirmation() {
  const location = useLocation()
  const navigate = useNavigate()
  const hubAppCopy = useHubAppCopy()
  const invoiceCopy = hubAppCopy('portalCopy')?.subscriptionCopy?.invoice?.confirmation
  const cardCopy = hubAppCopy('portalCopy')?.subscriptionCopy?.card?.confirmation
  const conversionCopy = cardCopy?.grantedConversion as GrantedConversionCopy | undefined
  const { user } = useApp()

  useHubSpotSnippet()
  useHubSpotConversion(user?.email)

  // Prefer router state (fresh navigation from the subscription modal). On a
  // hard refresh router state is lost, so we fall back to sessionStorage —
  // *but only when this page wasn't reached via a Stripe card return* (those
  // always carry ?subscriptionSuccess=true and should not adopt stale invoice
  // state from a prior session).
  const routerState = (location.state as CheckoutConfirmationState | null) ?? null
  const hasStripeReturnParams = location.search.includes('subscriptionSuccess')
  const [state] = useState<CheckoutConfirmationState | null>(
    () => routerState ?? (hasStripeReturnParams ? null : readStoredState())
  )

  const method: 'card' | 'invoice' = state?.method === 'invoice' ? 'invoice' : 'card'
  const isInvoice = method === 'invoice'

  // When nothing identifies the page — no router state, no sessionStorage,
  // no Stripe query params — render a generic "check your email" fallback
  // rather than silently showing the card UI (which would omit invoice info).
  const isDegraded = !isInvoice && state === null && !hasStripeReturnParams

  // Compute the validated href once. The value reaching `<a href={...}>` is
  // a fresh string derived from the URL constructor, never the raw input.
  const invoicePdfHref = safeHttpHref(state?.invoicePdfUrl)

  const [canContinue, setCanContinue] = useState(!isInvoice)

  useEffect(() => {
    if (!isInvoice) return
    setCanContinue(false)
    const timer = setTimeout(() => setCanContinue(true), INVOICE_MIN_DISPLAY_MS)
    return () => clearTimeout(timer)
  }, [isInvoice])

  const handleContinue = useCallback(() => {
    if (!canContinue) return

    const params = new URLSearchParams(location.search)
    params.set('subscriptionSuccess', 'true')
    const conversionFlash = grantedConversionFlash(params, conversionCopy)
    if (conversionFlash) params.set('flashMessage', conversionFlash)
    if (state?.flashMessage && !params.get('flashMessage')) {
      params.set('flashMessage', state.flashMessage)
    }

    try {
      sessionStorage.removeItem(CHECKOUT_CONFIRMATION_STORAGE_KEY)
    } catch {
      // ignore — clearing is best-effort
    }

    navigate(`/?${params.toString()}`)
  }, [canContinue, location.search, navigate, state?.flashMessage, conversionCopy])

  const pageTitle = isInvoice
    ? invoiceCopy?.pageTitle || 'Membership Confirmed - Prosci Portal'
    : cardCopy?.pageTitle || 'Membership Confirmed - Prosci Portal'

  const cardTitle = isInvoice
    ? invoiceCopy?.title || 'Your Membership is Active!'
    : isDegraded
      ? cardCopy?.fallback?.title || 'Your purchase is complete'
      : cardCopy?.title || "You're all set!"

  const cardSubtitle = isInvoice
    ? invoiceCopy?.subtitle ||
      'Thank you for choosing Prosci. Your membership is now active and you have full access to your plan benefits.'
    : isDegraded
      ? cardCopy?.fallback?.message ||
        'For your confirmation and any invoice or payment details, please check your email.'
      : cardCopy?.subtitle ||
        'Thank you for your purchase. Your membership is now active and you have full access to your plan benefits.'

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <main id="content">
        <ContentContainer>
          <Card className="mx-auto max-w-2xl">
            <CardHeader>
              <CardTitle>{cardTitle}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="prose max-w-none">
                <p>{cardSubtitle}</p>
              </div>

              {isInvoice && state?.planName && (
                <div className="bg-primary-50 rounded-lg p-4 text-center">
                  <span className="text-sm text-primary-700">
                    {invoiceCopy?.selectedPlan || 'Selected Plan'}
                  </span>
                  <p className="text-xl font-semibold text-primary-900">{state.planName}</p>
                </div>
              )}

              {isInvoice && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {invoiceCopy?.invoiceSection?.title || 'Invoice Details'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {state?.deferredStartDate
                      ? (invoiceCopy?.invoiceSection?.deferredDescription ||
                          'Your current access continues until your paid membership starts on {{startDate}}. We will email your invoice on that date, payable by bank transfer within the due date.'
                        ).replace('{{startDate}}', formatDueDate(state.deferredStartDate))
                      : (invoiceCopy?.invoiceSection?.description ||
                          'Your invoice has been generated and sent to your email. Please complete payment via bank transfer within the due date.')}
                  </p>

                  {state?.dueDate && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-yellow-800">
                        <span className="font-medium">
                          {invoiceCopy?.invoiceSection?.dueDateLabel || 'Payment Due:'}
                        </span>{' '}
                        {formatDueDate(state.dueDate)}
                      </p>
                    </div>
                  )}

                  {invoicePdfHref && (
                    <Button variant="outline" size="sm" asChild>
                      <a href={invoicePdfHref} target="_blank" rel="noopener noreferrer">
                        {invoiceCopy?.invoiceSection?.downloadPdf || 'Download PDF'}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              )}

              {isInvoice && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {invoiceCopy?.nextSteps?.title || 'Next Steps'}
                  </h3>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-600">
                    <li>
                      {invoiceCopy?.nextSteps?.step1 ||
                        'Check your email for the invoice with bank transfer details'}
                    </li>
                    <li>
                      {invoiceCopy?.nextSteps?.step2 ||
                        'Complete the bank transfer using the reference number provided'}
                    </li>
                    <li>
                      {invoiceCopy?.nextSteps?.step3 ||
                        'Your payment will be confirmed within 2-3 business days'}
                    </li>
                  </ol>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button
                  variant="default"
                  className="w-full"
                  onClick={handleContinue}
                  disabled={!canContinue}
                >
                  {isInvoice && !canContinue ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {invoiceCopy?.preparingDashboard || 'Preparing Dashboard...'}
                    </>
                  ) : (
                    (isInvoice ? invoiceCopy?.goToDashboard : cardCopy?.goToDashboard) ||
                    'Go to Dashboard'
                  )}
                </Button>

                {isInvoice && (
                  <p className="text-sm text-center text-gray-500">
                    {invoiceCopy?.questions || 'Questions about your invoice?'}{' '}
                    <a href="mailto:support@prosci.com" className="text-primary hover:underline">
                      {invoiceCopy?.contactSupport || 'Contact Support'}
                    </a>
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </ContentContainer>
        <Footer />
      </main>
    </>
  )
}

export default CheckoutConfirmation
